// AudioLiveCover.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';

import StatefulImage from '../component/StatefulImage.jsx';
import DecryptionWrapper from '../container/DecryptionWrapper.js';
import TextUserAvatar from '../container/TextUserAvatar.js';

import { getUserPicture, MediaAssetFormat } from '../resource/getMediaAsset.js';
import { color } from '../style/variables.js';

const AUDIO_LIVE_FALLBACK_AVATAR_SIZE = 80;

// Waveform design (per Figma): four rounded white bars of varied height /
// opacity, each pulsing on a staggered delay. Negative delays pre-seed each bar
// to a different phase so the wave is already mid-motion on the first frame —
// no start-up snap, and the loop stays seamless.
const AUDIO_LIVE_WAVE_BARS = [
  { x: 6, y: 9, height: 7.5, fillOpacity: 0.6, delay: 0 },
  { x: 12, y: 6, height: 12, fillOpacity: 1, delay: -0.225 },
  { x: 18, y: 10.5, height: 4.5, fillOpacity: 0.2, delay: -0.45 },
  { x: 24, y: 9, height: 7.5, fillOpacity: 0.9, delay: -0.675 },
];

const AudioLiveWave = ({ isMirrored = false }) => (
  <AudioLiveWaveSvg
    $isMirrored={isMirrored}
    width="33"
    height="26"
    viewBox="0 0 33 26"
    fill="none"
    aria-hidden
  >
    {AUDIO_LIVE_WAVE_BARS.map(({ x, y, height, fillOpacity, delay }) => (
      <AudioLiveWaveBar
        key={x}
        x={x}
        y={y}
        width="3"
        height={height}
        rx="1.5"
        fillOpacity={fillOpacity}
        $delay={delay}
      />
    ))}
  </AudioLiveWaveSvg>
);

AudioLiveWave.propTypes = {
  isMirrored: PropTypes.bool,
};

// Audio livestream cover: the streamer's avatar as a blurred, gradient
// background with a clear centered avatar flanked by animated waveforms.
const AudioLiveCover = ({
  userId = null,
  meId = null,
  username = null,
  avatarColor = null,
}) => {
  const avatarSource = useMemo(
    () =>
      getUserPicture({
        userId,
        meId,
        size: 256,
        format: MediaAssetFormat.JPG,
      }).href,
    [userId, meId]
  );

  return (
    <AudioLiveCoverWrapper>
      <AudioLiveBlurBackground $avatarColor={avatarColor}>
        <StatefulImage>
          <DecryptionWrapper resourceUrl={avatarSource}>
            <AudioLiveBlurImage src={avatarSource} alt="" />
          </DecryptionWrapper>
        </StatefulImage>
        <AudioLiveGradientOverlay />
      </AudioLiveBlurBackground>
      <AudioLiveForeground>
        <AudioLiveWave />
        <AudioLiveAvatarCircle>
          <StatefulImage>
            <AudioLiveAvatarFallback
              data-key="error"
              $avatarColor={avatarColor}
            >
              <TextUserAvatar
                userId={userId}
                avatarSize={AUDIO_LIVE_FALLBACK_AVATAR_SIZE}
              />
            </AudioLiveAvatarFallback>
            <DecryptionWrapper resourceUrl={avatarSource} data-key="target">
              <AudioLiveAvatarImage
                src={avatarSource}
                alt={username ? `${username}'s avatar` : ''}
              />
            </DecryptionWrapper>
          </StatefulImage>
        </AudioLiveAvatarCircle>
        <AudioLiveWave isMirrored />
      </AudioLiveForeground>
    </AudioLiveCoverWrapper>
  );
};

AudioLiveCover.propTypes = {
  userId: PropTypes.string,
  meId: PropTypes.string,
  username: PropTypes.string,
  avatarColor: PropTypes.string,
};

const AudioLiveCoverWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  overflow: hidden;
`;

const AudioLiveBlurBackground = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  background-color: ${({ $avatarColor }) => $avatarColor || color.neutral[0]};
  background-image: ${({ $avatarColor }) =>
    $avatarColor
      ? `linear-gradient(${color.neutral['0_alpha_40']}, ${color.neutral['0_alpha_40']})`
      : 'none'};
`;

const AudioLiveBlurImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(16px);
  transform: scale(1.2);
`;

const AudioLiveGradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
    to bottom,
    ${color.neutral['0_alpha_40']},
    transparent 40%,
    ${color.neutral['0_alpha_80']}
  );
`;

const AudioLiveForeground = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 8px;
`;

const AudioLiveAvatarCircle = styled.div`
  width: 40%;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
`;

const AudioLiveAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AudioLiveAvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $avatarColor }) => $avatarColor || color.neutral[0]};
`;

const audioLiveWaveBounce = keyframes`
  0%,
  100% {
    transform: scaleY(0.5);
  }
  50% {
    transform: scaleY(1);
  }
`;

const AudioLiveWaveSvg = styled.svg`
  display: block;
  flex-shrink: 0;
  filter: drop-shadow(0px 4px 4px ${color.neutral['0_alpha_20']});
  ${({ $isMirrored }) =>
    $isMirrored &&
    css`
      transform: scaleX(-1);
    `}
`;

const AudioLiveWaveBar = styled.rect`
  fill: ${color.neutral[100]};
  transform-box: fill-box;
  transform-origin: center;
  animation: ${audioLiveWaveBounce} 0.9s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`;

export { AudioLiveCover };
export default AudioLiveCover;
